import { Injectable } from '@angular/core';
import {
  CuadroPicnicGraphQLResponse,
  PicnicActivityDetailsGraphQLResponse,
  PicnicOrderReviewsGraphQLResponse,
  SetPicnicOrderReviewedGraphQLResponse,
  SetPicnicOrderReviewedInput,
} from '../../cuadro-picnic/models/cuadro-picnic.model';
import { GraphService } from './graph.service';

/**
 * Service managing GraphQL operations for Picnic and Cuadro Picnic.
 */
@Injectable({
  providedIn: 'root',
})
export class PicnicService {
  /**
   * Initializes the service with injected GraphService.
   *
   * @param graphService - Client wrapper for GraphQL endpoint requests.
   */
  constructor(private readonly graphService: GraphService) {}

  /**
   * Legacy query to retrieve picnic data by date from Operations GraphQL endpoint.
   *
   * @param date - Date filter string.
   * @returns Observable resolving to legacy picnic array.
   * @deprecated Se dejo de usar, se utiliza el queryGetCuadroPicnic
   */
  queryGetAllPicnic(date: string) {
    return this.graphService.queryGraphQLOperations<{
      getAllPicnic: any[];
    }>({
      query: `query GetAllPicnic($date: String!){
              getAllPicnic(date:$date) {
                id
                idFile
                fecha
                file
                group
                agencia
                actividad
                servicio
                categoriaServicio
                pax
                restriccionesAlimenticias
                alergias
                guiaPrincipal
                estadoFile
                guiaBike
                transportePax{
                  id
                  idProveedor
                  nombreProveedor
                  locacionProveedor
                  idServiceProveedor
                  nombreServiceProveedor
                  categoriaServicioProveedor
                }
                transportePicnic{
                  idProveedor
                  nombreProveedor
                  locacionProveedor
                  id
                  idServiceProveedor
                  nombreServiceProveedor
                  categoriaServicioProveedor
                }
                asistentePicnic {
                  idProveedor
                  id
                  nombreProveedor
                  locacionProveedor
                  idServiceProveedor
                  nombreServiceProveedor
                  categoriaServicioProveedor
                }
                banero{
                  idProveedor
                  id
                  nombreProveedor
                  locacionProveedor
                  idServiceProveedor
                  nombreServiceProveedor
                  categoriaServicioProveedor
                }
                tc
                obsPicnic,
                personaExtra
              }
            }`,
      variables: { date },
    });
  }

  /**
   * Queries Cuadro Picnic paginated dataset from Alimentos y Bebidas GraphQL API.
   *
   * @param filterInput - Filter criteria including itinerary ID, date range, keyword, food type, limit, and cursor.
   * @returns Observable resolving to CuadroPicnicGraphQLResponse.
   */
  queryGetCuadroPicnic(filterInput: {
    year?: string;
    idItinerary?: string;
    startDate?: string;
    endDate?: string;
    searchTerm?: string;
    foodType?: string;
    limit?: number;
    exclusiveStartKeyId?: string;
  }) {
    const input: Record<string, any> = {
      view: 'file',
      limit: filterInput.limit ?? 10,
      cursor: filterInput.exclusiveStartKeyId || undefined,
      sort: {
        field: 'updatedAt',
        direction: 'desc',
      },
      filters: {
        year: filterInput.year || new Date().getFullYear().toString(),
        idItinerary: filterInput.idItinerary || undefined,
        startDateFileFrom: filterInput.startDate || undefined,
        startDateFileTo: filterInput.endDate || undefined,
        search: filterInput.searchTerm || undefined,
        tipoAlimento: filterInput.foodType ? [filterInput.foodType] : undefined,
      },
    };

    return this.graphService.queryGraphQLCore<CuadroPicnicGraphQLResponse>({
      query: `query GetCuadroPicnicPage($input: GetPicnicFoodsInput!) {
        getCuadroPicnicPage(input: $input) {
          items {
            id
            idActivity
            idItinerary
            fechaOperacion
            fileNumber
            nameClient
            nombreItinerario
            isSSFFPadre
            reservationExecutives
            guiaPrincipal
            foodType
            activityName
            picnicDetails {
              paxCount
              tcCount
              driverCount
              guiaCount
              asistGuiaCount
              fieldStaffCount
              staffCount
              infantCount
              childCount
              dietaryRestrictionsSummary
            }
            empresaTransporte {
              totalCount
              generatedOrdersCount
              pendingOrdersCount
              preliminaryOrdersCount
            }
            asistentesPicnic {
              totalCount
              generatedOrdersCount
              pendingOrdersCount
              preliminaryOrdersCount
            }
            banero {
              totalCount
              generatedOrdersCount
              pendingOrdersCount
              preliminaryOrdersCount
            }
            operadorPicnic {
              totalCount
              generatedOrdersCount
              pendingOrdersCount
              preliminaryOrdersCount
            }
            isPicnicOrderReviewed
            revisadoBiblia
            observacionProgramadora
          }
          nextCursor
          totalCount
        }
      }`,
      variables: { input },
    });
  }

  /**
   * Queries dedicated detailed breakdown for a specific picnic activity modal.
   * Retrieves active passenger profiles and grouped AYB food services (including PAX and TC).
   *
   * @param detailsInput - Input object containing idActivity, optional idItinerary, and optional foodType.
   * @returns Observable resolving to PicnicActivityDetailsGraphQLResponse.
   */
  queryGetPicnicActivityDetails(detailsInput: {
    idActivity: string;
    idItinerary?: string;
    foodType?: string;
    exclusiveStartKeyId?: string;
    limit?: number;
  }) {
    return this.graphService.queryGraphQLAlimentosBebidas<PicnicActivityDetailsGraphQLResponse>(
      {
        query: `query GetPicnicActivityDetails($detailsInput: GetPicnicActivityDetailsInput!) {
          getPicnicActivityDetails(detailsInput: $detailsInput) {
            idActivity
            aybServices {
              destinatarioServicio
              nombreServicio
              nombreProveedor
              count
            }
            lastEvaluatedKeyId
          }
        }`,
        variables: { detailsInput },
      },
    );
  }

  /**
   * Queries operational services by activity ID and filters (tipoServicio, categoriasServicio)
   * directly from Core GraphQL API (api-explorandes-core).
   *
   * Description:
   *   Calls getServicesByActivityTipoCategoria query to fetch real-time services
   *   with full order details (orderId, orderNumber, orderStatus, scheduledPaymentDate, pdfStatus, etc.).
   *
   * Parameters:
   *   queryInputPayload: { idActivity: string; filters: Array<{ tipoServicio: string; categoriasServicio: string[] }> } - Filter parameters including target activity ID and operational category filters.
   *
   * Return Value:
   *   Observable resolving to array of service items with complete order metadata.
   *
   * Exceptions & Errors:
   *   Emits error through Observable if network request or Core GraphQL query fails.
   */
  queryGetServicesByActivityTipoCategoria(queryInputPayload: {
    idActivity: string;
    filters: Array<{
      tipoServicio: string;
      categoriasServicio: string[];
    }>;
  }) {
    return this.graphService.queryGraphQLCore<{
      getServicesByActivityTipoCategoria: Array<{
        id: string;
        nombreServicio: string;
        tipoServicio: string;
        categoriaServicio: string;
        ssffproduct?: boolean | null;
        serviceAdd?: boolean | null;
        productId?: string | null;
        providerSnapshot?: {
          id?: string;
          nombre?: string;
        };
        serviceSnapshot?: {
          locacion?: string;
        };
        order?: {
          orderId?: string;
          orderNumber?: string | number;
          displayCode?: string;
          orderStatus?: string;
          scheduledPaymentDate?: string;
          freezeDeadlineAt?: string;
          pdfStatus?: string;
          pdfGeneratedAt?: string;
          lastPdfError?: string;
        };
      }>;
    }>({
      query: `query GetServicesByActivityTipoCategoria(
        $input: GetServicesByActivityTipoCategoriaInput!
      ) {
        getServicesByActivityTipoCategoria(input: $input) {
          id
          nombreServicio
          tipoServicio
          categoriaServicio
          ssffproduct
          serviceAdd
          productId
          providerSnapshot {
            id
            nombre
          }
          serviceSnapshot {
            locacion
          }
          order {
            orderId
            orderNumber
            displayCode
            orderStatus
            scheduledPaymentDate
            freezeDeadlineAt
            pdfStatus
            pdfGeneratedAt
            lastPdfError
          }
        }
      }`,
      variables: { input: queryInputPayload },
    });
  }

  /**
   * Updates observation notes for an activity on Cuadro Picnic.
   *
   * @param activityId - Unique identifier of the activity.
   * @param observaciones - Observation text note.
   * @returns Observable resolving to mutation response.
   * @deprecated Se dejó de usar, se utiliza el mutationUpdatePicnicObservation
   */
  mutationUpdatePicnicObservation(activityId: string, observaciones: string) {
    return this.graphService.mutationAybApi<{
      updatePicnicObservation: { success: boolean; message?: string };
    }>({
      mutation: `mutation UpdatePicnicObservation($updateInput: UpdatePicnicObservationInput!) {
        updatePicnicObservation(updateInput: $updateInput) {
          success
          message
        }
      }`,
      variables: { updateInput: { activityId, observaciones } },
    });
  }

  /**
   * Updates assigned service supplier on Cuadro Picnic .
   *
   * @param serviceId - Unique service ID.
   * @param providerId - Sourcing or AyB provider ID.
   * @param providerName - Name of the provider.
   * @returns Observable resolving to mutation response.
   */
  mutationUpdatePicnicServiceSupplier(
    serviceId: string,
    providerId: string,
    providerName: string,
  ) {
    return this.graphService.mutationAybApi<{
      updatePicnicServiceSupplier: { success: boolean; message?: string };
    }>({
      mutation: `mutation UpdatePicnicServiceSupplier($updateInput: UpdateServiceSupplierInput!) {
        updatePicnicServiceSupplier(updateInput: $updateInput) {
          success
          message
        }
      }`,
      variables: {
        updateInput: { serviceId, providerId, providerName },
      },
    });
  }

  /**
   * Queries picnic order reviews for a specific itinerary directly from Core GraphQL API (api-explorandes-core).
   *
   * Description:
   *   Invokes the getPicnicOrderReviewsByItinerary query on Core API (/core/graphql) to retrieve
   *   the canonical review verification statuses ('Revisado Pedido') for all food activities of an itinerary.
   *
   * Parameters:
   *   idItinerary: string - Target itinerary unique identifier.
   *
   * Return Value:
   *   Observable<PicnicOrderReviewsGraphQLResponse> - Observable resolving to the response containing the array of picnic order review items.
   *
   * Exceptions & Errors:
   *   Emits error through Observable if network request or Core GraphQL query fails.
   */
  public queryGetPicnicOrderReviewsByItinerary(idItinerary: string) {
    return this.graphService.queryGraphQLCore<PicnicOrderReviewsGraphQLResponse>(
      {
        query: `query GetPicnicOrderReviewsByItinerary($idItinerary: String!) {
        getPicnicOrderReviewsByItinerary(idItinerary: $idItinerary) {
          idActivity
          tipoAlimento
          idItinerary
          isReviewed
          reviewedAt
          reviewedBy
        }
      }`,
        variables: { idItinerary },
      },
    );
  }

  /**
   * Updates the review status ('Revisado Pedido') for a specific activity food type on Core GraphQL API (api-explorandes-core).
   *
   * Description:
   *   Dispatches setPicnicOrderReviewed mutation against api-explorandes-core to update the isReviewed flag,
   *   attaching the reviewer's authenticated Cognito email and setting the review timestamp.
   *
   * Parameters:
   *   inputPayload: SetPicnicOrderReviewedInput - Review mutation payload containing idActivity, tipoAlimento, isReviewed, and optional reviewedBy.
   *
   * Return Value:
   *   Observable<SetPicnicOrderReviewedGraphQLResponse> - Observable resolving to response containing updated review item state.
   *
   * Exceptions & Errors:
   *   Throws error if the review item is not found in Core (404) or network mutation fails.
   */
  public mutationSetPicnicOrderReviewed(
    inputPayload: SetPicnicOrderReviewedInput,
  ) {
    return this.graphService.mutateGraphQLCore<SetPicnicOrderReviewedGraphQLResponse>(
      {
        mutation: `mutation SetPicnicOrderReviewed($input: SetPicnicOrderReviewedInput!) {
        setPicnicOrderReviewed(input: $input) {
          idActivity
          tipoAlimento
          idItinerary
          isReviewed
          reviewedAt
          reviewedBy
        }
      }`,
        variables: { input: inputPayload },
      },
    );
  }

  /**
   * Updates the review status ('Revisado Pedido') for a specific food type on an activity in Cuadro Picnic.
   *
   * Description:
   *   Legacy mutation against alimentos-bebidas-api. Canonical implementation is mutationSetPicnicOrderReviewed on Core API.
   *
   * Parameters:
   *   idActivity: string - Activity unique identifier.
   *   tipoAlimento: string - Food type category ('ALIM TREK', 'PICNIC', 'BOX LUNCH', 'SNACK').
   *   isReviewed: boolean - Target boolean status.
   *
   * Return Value:
   *   Observable resolving to mutation response with success and message.
   *
   * Exceptions & Errors:
   *   Emits error on GraphQL mutation or network failure.
   *
   * @deprecated Usar mutationSetPicnicOrderReviewed contra api-explorandes-core.
   */
  public updatePicnicOrderReviewStatus(
    idActivity: string,
    tipoAlimento: string,
    isReviewed: boolean,
  ) {
    return this.graphService.mutationAybApi<{
      updatePicnicOrderReviewStatus: { success: boolean; message?: string };
    }>({
      mutation: `mutation UpdatePicnicOrderReviewStatus($updateInput: UpdatePicnicOrderReviewInput!) {
        updatePicnicOrderReviewStatus(updateInput: $updateInput) {
          success
          message
        }
      }`,
      variables: {
        updateInput: {
          idActivity,
          tipoAlimento,
          isReviewed,
        },
      },
    });
  }

  /**
   * Legacy observation update mutation.
   * @deprecated Se dejó de usar, se utiliza el mutationUpdatePicnicObservation
   */
  mutationChangeObservacionPicnic(idServicio: string, obsPicnic: string) {
    return this.graphService.mutateGraphQLOperations<{
      changeObservacionPicnic: any;
    }>({
      mutation: `mutation changeObservacionPicnic($idServicio: String!,$obsPicnic: String!){
        changeObservacionPicnic(idServicio:$idServicio,obsPicnic:$obsPicnic)
      }`,
      variables: { idServicio, obsPicnic },
    });
  }

  /**
   * Legacy extra person mutation.
   * @deprecated Se dejó de usar, se utiliza el mutationUpdatePicnicServiceSupplier
   */
  addPersonaExtra(idServicio: string, cantPersona: number) {
    return this.graphService.mutateGraphQLOperations<{
      addPersonaExtra: any;
    }>({
      mutation: `mutation AddPersonaExtra($idServicio:String!,$cantPersona:Float!){
        addPersonaExtra(idServicio:$idServicio,cantPersona:$cantPersona)
      }`,
      variables: { idServicio, cantPersona },
    });
  }
}
